import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Badge,
  Paper,
  Divider,
  DialogActions,
} from "@mui/material";
import {
  PersonSearch,
  CheckCircleOutline,
  Email,
  WhatsApp,
  ArrowBack,
} from "@mui/icons-material";
import InputMask from "react-input-mask";
import { Link, useNavigate } from "react-router-dom";
import { api_login_unico_url, app_base_url } from "../../../Utils/Urls";
import { Api } from "../../../Utils/Api";
import { useEntidade } from "../../../Hooks/EntidadeHook";
import { Logo } from "../../../Components/Logo";
import { IUsuario } from "../../../Hooks/UsuarioHook";

const normalizarTexto = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();


export const RecuperarSenha = () => {
  const [tipoPessoa, setTipoPessoa] = useState<"cpf" | "cnpj">("cpf");
  const [documento, setDocumento] = useState("");
  const [erro, setErro] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [usuario, setUsuario] = useState("");
  const [contaSelecionada, setContaSelecionada] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { entidades } = useEntidade();
  const [openAvisoContato, setOpenAvisoContato] = useState(false);


  const temEmail = !!contaSelecionada?.email;
  const temWhatsapp = !!contaSelecionada?.telefone;


  const navigate = useNavigate();

  const mascararEmail = (email: string) => {
    if (!email) return "Não informado";

    const [usuario, dominio] = email.split("@");

    if (usuario.length <= 4) {
      return `${usuario[0]}***${usuario[usuario.length - 1]}@${dominio}`;
    }

    return `${usuario.substring(0, 3)}***${usuario.substring(
      usuario.length - 2
    )}@${dominio}`;
  };

  const mascararTelefone = (telefone: string) => {
    if (!telefone) return "Não informado";
    return `55 ${telefone.substring(0, 2)} 9 ****-${telefone.substring(7)}`;
  };

  const handleRecuperarSenha = async () => {
    setErro("");
    setLoading(true);

    try {
      const { data } = await Api.get(
        `/por-cpf-cnpj/${documento.replace(/\D/g, "")}`
      );
      setUsuarios(data.data);
      setOpenDialog(true);
    } catch (error) {
      setErro("Erro ao buscar usuários. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelecionarConta = async (usuario: any) => {

    if (!usuario.email && !usuario.telefone) {
      setContaSelecionada(usuario);
      setOpenAvisoContato(true);
      return;
    }
    setContaSelecionada(usuario);

    const params = { id_usuario: usuario.id };

    try {
      await Api.post(`/recupera-senha`, params);
      setOpenDialog(false);
      setOpenConfirm(true);
    } catch (e) {
      setErro("Erro ao solicitar recuperação de senha.");
    }

    setOpenDialog(false);
    setOpenConfirm(true);
  };

  const [filtroEntidade, setFiltroEntidade] = useState<
    "prefeitura" | "camara" | "consorcio" | "todos"
  >("prefeitura");

  const [buscaEntidade, setBuscaEntidade] = useState("");

  const usuariosFiltrados = usuarios.filter((usuario) => {
    const entidade = entidades.find((e) => e.id === usuario.entidade_id);
    if (!entidade) return false;

    const tituloNormalizado = normalizarTexto(entidade.titulo);

    // filtro por tipo
    if (filtroEntidade !== "todos") {
      if (
        filtroEntidade === "prefeitura" &&
        !tituloNormalizado.includes("prefeitura")
      )
        return false;

      if (
        filtroEntidade === "camara" &&
        !tituloNormalizado.includes("camara")
      )
        return false;

      if (
        filtroEntidade === "consorcio" &&
        !tituloNormalizado.includes("consorcio")
      )
        return false;
    }

    // filtro por busca
    if (buscaEntidade) {
      if (
        !tituloNormalizado.includes(normalizarTexto(buscaEntidade))
      )
        return false;
    }

    return true;
  });


  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{ height: "100vh" }}
    >
      <Grid item xs={11} sm={8} md={6} lg={4}>
        <Box
          sx={{ p: 4, borderRadius: 3, boxShadow: 3, bgcolor: "background.paper", border: 1, borderColor: "divider" }}
        >
          {/* Logo da empresa */}
          <Box display="flex" justifyContent="center" mb={2}>
            <Logo />
          </Box>

          <Typography
            variant="h5"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
          >
            Recuperação de Conta
          </Typography>

          <Typography textAlign="center" color="text.secondary" sx={{ mb: 2 }}>
            Informe seu CPF ou CNPJ para receber o link de recuperação de senha.
          </Typography>

          <RadioGroup
            row
            value={tipoPessoa}
            onChange={(e) => setTipoPessoa(e.target.value as "cpf" | "cnpj")}
            sx={{ justifyContent: "center", mb: 2 }}
          >
            <FormControlLabel value="cpf" control={<Radio />} label="CPF" />
            <FormControlLabel value="cnpj" control={<Radio />} label="CNPJ" />
          </RadioGroup>

          <InputMask
            mask={
              tipoPessoa === "cpf" ? "999.999.999-99" : "99.999.999/9999-99"
            }
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
            maskChar=""
          >
            {() => (
              <TextField
                fullWidth
                label={
                  tipoPessoa === "cpf" ? "Digite seu CPF" : "Digite seu CNPJ"
                }
                variant="outlined"
                margin="normal"
                error={!!erro}
                helperText={erro}
              />
            )}
          </InputMask>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 2, py: 1.5 }}
            onClick={handleRecuperarSenha}
            disabled={loading}
            startIcon={<PersonSearch />}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Buscar Contas"
            )}
          </Button>
          <Grid container justifyContent="center">
            <Box textAlign="center" mt={2}>
              <Button
                sx={{
                  textTransform: "none",
                  color: "#1976D2",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: 1, // Espaçamento entre o ícone e o texto
                }}
                startIcon={<ArrowBack />} // Ícone de voltar
                onClick={() => navigate(-1)}
              >
                Voltar
              </Button>
            </Box>
          </Grid>
        </Box>
      </Grid>

      {/* Modal de Seleção de Usuário */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          🔍 Selecione sua Conta
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <RadioGroup
              row
              value={filtroEntidade}
              onChange={(e) =>
                setFiltroEntidade(e.target.value as any)
              }
              sx={{ justifyContent: "center" }}
            >
              <FormControlLabel value="prefeitura" control={<Radio />} label="Prefeitura" />
              <FormControlLabel value="camara" control={<Radio />} label="Câmara" />
              <FormControlLabel value="consorcio" control={<Radio />} label="Consórcio" />
              <FormControlLabel value="todos" control={<Radio />} label="Todos" />
            </RadioGroup>

            <TextField
              fullWidth
              size="small"
              placeholder="Pesquisar entidade..."
              value={buscaEntidade}
              onChange={(e) => setBuscaEntidade(e.target.value)}
            />
          </Box>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {usuariosFiltrados.map((usuario) => {
              const entidade = entidades.find(
                (e) => e.id === usuario.entidade_id
              );
              return (
                <Grid item xs={12} key={usuario.id}>
                  <Card
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 2,
                      borderRadius: 3,
                      boxShadow: 5,
                      "&:hover": { transform: "scale(1.02)", boxShadow: 6 },
                      cursor: "pointer",
                      transition: "0.2s",
                    }}
                    onClick={() => handleSelecionarConta(usuario)}
                  >
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        entidade ? (
                          <Avatar
                            src={entidade?.logo}
                            sx={{
                              width: 24,
                              height: 24,
                              border: "2px solid #fff",
                            }}
                          />
                        ) : null
                      }
                    >
                      <Avatar
                        src={`${api_login_unico_url}/foto-de-perfil/${usuario.foto}`}
                        sx={{
                          width: 65,
                          height: 65,
                          mr: 2,
                          border: "4px solid #1976D2",
                        }}
                      >
                        {usuario.nome.charAt(0)}
                      </Avatar>
                    </Badge>
                    <CardContent sx={{ p: 0 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {usuario.nome}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {entidade?.titulo || "Entidade não encontrada"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Divider />
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button
              variant="outlined"
              onClick={() => setOpenDialog(false)}
              sx={{ px: 4 }}
            >
              Fechar
            </Button>

          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação */}
      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          {/* Ícone de sucesso */}
          <CheckCircleOutline sx={{ fontSize: 70, color: "#4CAF50", mb: 2 }} />

          {/* Título mais chamativo */}
          <Typography
            variant="h6"
            fontWeight="600"
            sx={{ color: "#333", mb: 2 }}
          >
            {temEmail
              ? "Link de recuperação enviado!"
              : "Recuperação enviada via WhatsApp"}
          </Typography>
{!temEmail && temWhatsapp && (
  <Typography
    variant="body1"
    sx={{ mb: 2, color: "warning.main", fontWeight: "bold" }}
  >
    O envio foi realizado apenas via WhatsApp.
    <br />
    Caso não receba a nova senha, solicite ao suporte.
  </Typography>
)}




          {/* Exibe o card de e-mail apenas se houver e-mail cadastrado */}
          {contaSelecionada?.email && (
            <Paper
              elevation={3}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: "rgba(33,150,243,0.12)",
                border: "1px solid rgba(33,150,243,0.4)",
                mb: 2,
              }}
            >
              <Email sx={{ color: "#1E88E5", fontSize: 28 }} />
              <Typography fontSize={16} color="#1E88E5">
                {mascararEmail(contaSelecionada.email)}
              </Typography>
            </Paper>
          )}

          {/* Exibe o card de WhatsApp apenas se houver telefone cadastrado */}
          {contaSelecionada?.telefone && (
            <Paper
              elevation={3}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: "rgba(76,175,80,0.12)",
                border: "1px solid rgba(76,175,80,0.4)",
              }}
            >
              <WhatsApp sx={{ color: "#43A047", fontSize: 28 }} />
              <Typography fontSize={16} color="#43A047">
                {mascararTelefone(contaSelecionada.telefone)}
              </Typography>
            </Paper>
          )}

          {/* Botão OK aprimorado */}
          <Button
            variant="contained"
            sx={{
              mt: 3,
              px: 4,
              py: 1,
              bgcolor: "primary.main",
              "&:hover": { bgcolor: "primary.dark" },
            }}
            onClick={() => {
              setOpenConfirm(false);
              navigate(`${app_base_url}`);
            }}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openAvisoContato}
        onClose={() => setOpenAvisoContato(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <PersonSearch sx={{ fontSize: 70, color: "#FB8C00", mb: 2 }} />

          <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
            Não foi possível recuperar sua senha
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Esta conta não possui e-mail nem telefone cadastrados para envio do link
            de recuperação.
            <br />
            Entre em contato com o suporte para atualizar seus
            dados.
          </Typography>

          <Button
            variant="contained"
            color="warning"
            sx={{ px: 4 }}
            onClick={() => {
              setOpenAvisoContato(false);
              setOpenDialog(true); // volta para a lista de contas
            }}
          >
            Voltar
          </Button>
        </DialogContent>
      </Dialog>

    </Grid>
  );
};
